import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import ServiceCard from "@/components/marketplace/ServiceCard";
import ProviderCard from "@/components/marketplace/ProviderCard";
import api from "@/lib/api";

export default function SearchResultsPage() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/search", { params: { q: sp.get("q") || "", scope: "all" } });
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [sp]);

  const submit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(sp);
    if (q) next.set("q", q); else next.delete("q");
    setSp(next, { replace: false });
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Search</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-[#071b22] tracking-tight" data-testid="search-title">
          Results for "{sp.get("q") || "—"}"
        </h1>
        <form onSubmit={submit} className="mt-6 flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 max-w-xl" data-testid="search-form">
          <Search className="h-4 w-4 text-[#165a71]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search services, providers, categories…"
            className="flex-1 py-2 outline-none bg-transparent text-sm"
            data-testid="search-input"
          />
          <button type="submit" className="rounded-full bg-[#165a71] text-white px-4 py-1.5 text-sm font-medium" data-testid="search-submit">Search</button>
        </form>

        {loading || !data ? (
          <div className="mt-10 text-[#252A34]/60" data-testid="search-loading">Searching…</div>
        ) : (
          <div className="mt-10 space-y-14">
            <section>
              <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-4">Services <span className="text-[#252A34]/60 text-base font-normal">({data.services_total || 0})</span></h2>
              {data.services?.length === 0 ? (
                <div className="text-[#252A34]/60 text-sm">No matching services.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="search-services">
                  {data.services.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
                </div>
              )}
            </section>
            <section>
              <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-4">Providers <span className="text-[#252A34]/60 text-base font-normal">({data.providers_total || 0})</span></h2>
              {data.providers?.length === 0 ? (
                <div className="text-[#252A34]/60 text-sm">No matching providers.</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="search-providers">
                  {data.providers.map((p, i) => <ProviderCard key={p.id} provider={p} index={i} />)}
                </div>
              )}
            </section>
            <section>
              <h2 className="font-display text-2xl text-[#071b22] font-semibold mb-4">Categories</h2>
              <div className="flex flex-wrap gap-2" data-testid="search-categories">
                {(data.categories || []).map((c) => (
                  <Link key={c.id} to={`/categories/${c.slug}`} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm hover:border-[#165a71]/40">
                    {c.name}
                  </Link>
                ))}
                {(!data.categories || data.categories.length === 0) && <span className="text-[#252A34]/60 text-sm">No matching categories.</span>}
              </div>
            </section>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
